/**
 * Records — the CRUD surface over the object catalogue.
 *
 * The object list is built from fin.meta_objects, not hardcoded: adding a
 * table to the catalogue puts it here without a code change.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import RecordsTable from './RecordsTable';

interface ObjectMeta {
  slug: string;
  label: string;
  mode: 'editable' | 'overlay' | 'readonly';
  attribute_count: number;
  editable_count: number;
}

const GROUPS: { mode: ObjectMeta['mode']; title: string; hint: string }[] = [
  { mode: 'editable', title: 'Master data', hint: 'The app is the system of record' },
  { mode: 'overlay', title: 'From source systems', hint: 'Values are read-only; classification is yours' },
  { mode: 'readonly', title: 'Derived', hint: 'Calculated by the pipeline' },
];

export default function RecordsPage() {
  const { object } = useParams<{ object?: string }>();
  const navigate = useNavigate();
  const [objects, setObjects] = useState<ObjectMeta[] | null>(null);

  useEffect(() => {
    fetch('/api/fin/objects', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setObjects(d?.objects ?? []))
      .catch(() => setObjects([]));
  }, []);

  const grouped = useMemo(() => {
    const out = new Map<string, ObjectMeta[]>();
    for (const g of GROUPS) out.set(g.mode, (objects ?? []).filter((o) => o.mode === g.mode));
    return out;
  }, [objects]);

  // Land on the first object rather than an empty pane.
  useEffect(() => {
    if (!object && objects?.length) navigate(`/records/${objects[0].slug}`, { replace: true });
  }, [object, objects, navigate]);

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      <aside className="w-56 shrink-0 border-r">
        <ScrollArea className="h-full">
          <div className="p-2">
            {!objects && (
              <div className="space-y-1.5 p-2">
                {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}
              </div>
            )}
            {GROUPS.map((g, gi) => {
              const items = grouped.get(g.mode) ?? [];
              if (!items.length) return null;
              return (
                <div key={g.mode}>
                  {gi > 0 && <Separator className="my-2" />}
                  <p className="px-2 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {g.title}
                  </p>
                  <p className="px-2 pb-1.5 text-[10px] leading-tight text-muted-foreground/70">
                    {g.hint}
                  </p>
                  {items.map((o) => (
                    <button
                      key={o.slug}
                      onClick={() => navigate(`/records/${o.slug}`)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left',
                        'hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                        object === o.slug && 'bg-accent font-medium'
                      )}
                    >
                      <span className="flex-1 truncate">{o.label}</span>
                      {o.editable_count > 0 && (
                        <Badge variant="secondary" className="h-4 px-1 text-[9px] tabular-nums">
                          {o.editable_count}
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </aside>

      <main className="flex-1 min-w-0">
        {object ? <RecordsTable object={object} /> : null}
      </main>
    </div>
  );
}
